/*
 *==================================================
 * Licensed Materials - Property of HCL Technologies
 *
 * HCL Commerce
 *
 * (C) Copyright HCL Technologies Limited 2021
 *
 *==================================================
 */
//Standard libraries
import attempt from "lodash/attempt";
import isError from "lodash/isError";
// import { FOR_USER_SESSION } from "../constants/common";

const KEY_PREFIX = `HCS-CSR-`;

const getStorageHelper = (isSession: boolean) => {
  const storage = isSession ? sessionStorage : localStorage;
  const EXPIRATION_SUFFIX = "STORAGEUTIL-EXPIREKEY";
  const EXPIRATION_DAY: number = 24 * 60 * 60 * 1000;
  const DEFAULT_EXPIRATION_DAYS: number = 30;

  /**
   * Gets the item from localStorage.
   * @param {string} key
   * @returns {any | null}
   */
  const get = (key: string): any | null => {
    const r = storage.getItem(`${KEY_PREFIX}${key}`);
    let result = r == null ? null : attempt(JSON.parse, r);
    if (isError(result)) {
      result = r;
    }
    return result;
  };

  /**
   * Gets the keys from localStorage where key starts with prefix
   * @param {string} key
   * @returns {any[]}
   */
  const getKeysStartsWith = (keyStartsWith: string): any | null => {
    let getKeysList: any[] = [];
    for (let i = 0; i < storage.length; i++) {
      let key = storage.key(i);
      if (key && key.startsWith(`${KEY_PREFIX}${keyStartsWith}`)) {
        getKeysList.push(key.substring(key.indexOf(`${keyStartsWith}`)));
      }
    }
    return getKeysList;
  };

  /**
   * Save the item to localStorage
   * @param {string} key
   * @param {string} value
   * @param {number} expiration duration in days
   * @param {boolean} notExpire
   */
  const set = (
    key: string,
    value: any,
    expiration?: number,
    notExpire?: boolean
  ) => {
    remove(key);
    const currentKey = `${KEY_PREFIX}${key}`;
    setItem(currentKey, value);
    if (!isSession) {
      if (expiration) {
        setItem(
          `${KEY_PREFIX}` + getExpirationKey(key),
          getExpireValue(expiration)
        );
      } else if (!notExpire) {
        setItem(
          `${KEY_PREFIX}` + getExpirationKey(key),
          getExpireValue(DEFAULT_EXPIRATION_DAYS)
        );
      }
    }
  };

  /**
   * Remove the localStorage cache item with specific key
   * @param {string} key
   */
  const remove = (key: string) => {
    storage.removeItem(`${KEY_PREFIX}${key}`);
    storage.removeItem(`${KEY_PREFIX}` + getExpirationKey(key));
  };

  /**
   * Remove the localStorage cache item with key starting with prefix
   * @param {string} key
   */
  const removeStartsWith = (keyStartsWith: string) => {
    let removeKeysList: any[] = [];
    for (let i = 0; i < storage.length; i++) {
      let key = storage.key(i);
      if (key && key.startsWith(`${KEY_PREFIX}${keyStartsWith}`)) {
        removeKeysList.push(key.substring(key.indexOf(`${keyStartsWith}`)));
      }
    }
    removeKeysList.forEach((key) => remove(key));
  };

  /**
   * get the key that stores the expiration days info
   * @param {string} key
   * @returns {string} the key for storing expiration days
   */
  const getExpirationKey = (key: string) => {
    return `${key}-${EXPIRATION_SUFFIX}`;
  };

  /**
   * Invalidate the localStorage cache if it is expired.
   * @param {string} key
   */
  const invalidateIfExpired = (key: string): boolean => {
    const expireKey = getExpirationKey(key);
    const expireTime = get(expireKey);
    const currentTime = new Date().getTime();
    if (expireTime && currentTime > parseInt(expireTime, 10)) {
      remove(key);
      return true;
    } else {
      return false;
    }
  };

  const invalidateAllIfExpired = () => {
    const keys: string[] = getStorageKeys();
    keys.forEach((k) => invalidateIfExpired(k));
  };

  const getExpireValue = (t: number) => {
    return String(new Date().getTime() + t * EXPIRATION_DAY);
  };

  const getStorageKeys = (): string[] => {
    const keyList = [];
    for (let i = 0; i < storage.length; i++) {
      let key = storage.key(i);
      if (
        key &&
        key.startsWith(KEY_PREFIX) &&
        !key.endsWith(EXPIRATION_SUFFIX)
      ) {
        keyList.push(key.substr(KEY_PREFIX.length));
      }
    }
    return keyList;
  };

  const setItem = (key: string, value: any) => {
    try {
      storage.setItem(key, JSON.stringify(value));
    } catch (e) {
      console.warn(e);
    }
  };

  const setForUserSession = (key: string, forUser: any) => {
    setItem(key, forUser);
  };

  const removeForUserSession = (key: string) => {
    storage.removeItem(key);
  };

  if (isSession) {
    return {
      get,
      getKeysStartsWith,
      set,
      remove,
      removeStartsWith,
      setForUserSession,
      removeForUserSession,
      invalidateAllIfExpired: () => {},
    };
  } else {
    return {
      get,
      getKeysStartsWith,
      set,
      remove,
      removeStartsWith,
      removeForUserSession: (key: string) => {},
      setForUserSession: (key: string, forUser: any) => {},
      invalidateAllIfExpired,
    };
  }
};

const localStorageHelper = getStorageHelper(false);
const sessionStorageHelper = getStorageHelper(true);
export { localStorageHelper, sessionStorageHelper };
